import {type Active} from '@dnd-kit/core';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Callout} from '@wandb/weave/components/Callout';
import {type IconName} from '@wandb/weave/components/Icon';
import {type TagColorName} from '@wandb/weave/components/Tag';
import React, {useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {useSelector} from '../../../state/hooks';
import {type Ref as PanelRef} from '../../../state/views/panel/types';
import {DEFAULT_PANEL_SIZE} from '../../../util/panelbankTypes';
import {getPanelSpec} from '../../../util/panelTypes';
import {type PanelViews, panelViews} from '../../PanelBank/types';

/**
 * This is a presentational component that is used to display the drag overlay for a panel.
 * This should be decoupled from the panel content.
 * https://docs.dndkit.com/api-documentation/draggable/drag-overlay#patterns
 */
export const WorkspacePanelDragOverlay = ({
  activeItem,
}: {
  activeItem: Active | null;
}) => {
  if (!activeItem || activeItem?.data.current?.type !== 'panel') {
    return null;
  }

  return (
    <div
      className={twMerge(
        'cursor-grabbing',
        'rounded border',
        '!h-fit !p-12',
        'bg-white',
        'border-moon-250 shadow-[0px_12px_24px_0px_rgba(13,15,18,0.16)]'
      )}
      style={{
        height: DEFAULT_PANEL_SIZE,
        width: DEFAULT_PANEL_SIZE,
      }}>
      <WorkspacePanelOverlayContent
        panelRef={activeItem.data.current.panelRef}
      />
    </div>
  );
};

const WorkspacePanelOverlayContent = ({panelRef}: {panelRef: PanelRef}) => {
  const {viewType, config} = useSelector(state => {
    const panel = state.views.parts[panelRef.type][panelRef.id];
    return {
      viewType: panel?.viewType,
      config: panel?.config,
    };
  });

  const {iconName, iconColor} = useMemo(() => {
    return PANEL_VIEW_TYPE_TO_ICON[viewType as PanelViews] || DEFAULT_ICON;
  }, [viewType]);

  const title = useMemo(() => {
    const {getTitleFromConfig} = getPanelSpec(viewType);
    return getTitleFromConfig?.(config as any);
  }, [viewType, config]);

  const displayTitle = title || viewType || 'Panel';

  return (
    <div className="flex items-center gap-8">
      <Callout size="x-small" icon={iconName} color={iconColor} />
      <div
        className="flex overflow-hidden text-sm font-semibold text-moon-800"
        style={{
          width: DEFAULT_PANEL_SIZE - 50,
          overflowWrap: 'anywhere',
          maxHeight: DEFAULT_PANEL_SIZE - 50,
        }}>
        {displayTitle}
      </div>
    </div>
  );
};

const DEFAULT_ICON: {
  iconName: IconName;
  iconColor: TagColorName;
} = {
  iconName: 'line-plot-alt2',
  iconColor: 'blue',
};

// This is a mapping of panel view type to icons that match what's used in the
// add panels drawer. It currently doesn't filter down to the specific panel type,
// but something we should consider in the future.
const PANEL_VIEW_TYPE_TO_ICON: Record<
  PanelViews,
  {iconName: IconName; iconColor: TagColorName}
> = {
  [panelViews.barChart]: {
    iconName: 'chart-vertical-bars',
    iconColor: 'blue', // Charts group
  },
  [panelViews.codeComparer]: {
    iconName: 'code-alt',
    iconColor: 'teal', // Text and code group
  },
  [panelViews.markdown]: {
    iconName: 'text-language',
    iconColor: 'teal', // Text and code group
  },
  [panelViews.marimo]: {
    iconName: 'heart',
    iconColor: 'blue', // Charts group
  },
  [panelViews.mediaBrowser]: {
    iconName: 'photo',
    iconColor: 'green', // Media group
  },
  [panelViews.multiRunTable]: {
    iconName: 'table',
    iconColor: 'blue', // Not in menu, keeping blue
  },
  [panelViews.parallelCoordinatesPlot]: {
    iconName: 'parallel-coordinates',
    iconColor: 'blue', // Charts group
  },
  [panelViews.parameterImportance]: {
    iconName: 'code-alt',
    iconColor: 'purple', // Evaluation group
  },
  [panelViews.runComparer]: {
    iconName: 'table',
    iconColor: 'purple', // Evaluation group
  },
  [panelViews.runHistoryLinePlot]: {
    iconName: 'line-plot-alt2',
    iconColor: 'blue', // Charts group
  },
  [panelViews.scalarChart]: {
    iconName: 'type-number-alt',
    iconColor: 'blue', // Charts group
  },
  [panelViews.scatterPlot]: {
    iconName: 'chart-scatterplot',
    iconColor: 'blue', // Charts group
  },
  [panelViews.vega]: {
    iconName: 'category-multimodal',
    iconColor: 'blue', // Legacy vega, keeping consistent with vega2
  },
  [panelViews.vega2]: {
    iconName: 'category-multimodal',
    iconColor: 'blue', // Charts group
  },
  [panelViews.vega3]: {
    iconName: 'category-multimodal',
    iconColor: 'blue', // Keeping consistent with vega/vega2
  },
  [panelViews.weave]: {
    iconName: 'search', // panel-weave uses search icon
    iconColor: 'magenta', // Query panels group
  },
  [panelViews.weaveTrace]: {
    iconName: 'layout-tabs',
    iconColor: 'sienna',
  },
} as const;
